// ============================================================================
// src/lib/access.js — the app's two access predicates.
// ============================================================================
// B2.2c: moved verbatim out of App(). They are the rules Header's program
// strip and App's route gate both consult, and they had to be App-local props
// for as long as App owned them -- which is what forced every view mount to be
// handed a Header component prop in turn.
//
// They deliberately do NOT live in lib/globalNav.js: that module's contract,
// pinned by global_nav_static.test.js, is that the nav config DELEGATES
// permission decisions and never restates a role rule of its own beyond
// PROCESSING_ROLES. These functions ARE role rules, so they get their own
// module and globalNav keeps its property.
//
// canAccessProgram takes authState explicitly; inside App it read the closure.
// Nothing else about either function changed.
// ============================================================================

export interface AccessProfile {
  program_access?: string[] | null;
}

export interface AuthSession {
  role?: string | null;
  profile?: AccessProfile | null;
}

export type AuthState = AuthSession | false | null | undefined;

interface ViewAllowlist extends ReadonlySet<string> {
  has(view: string | undefined): boolean;
}

// Light-user containment allowlist. A real authenticated Light role is a field
// user limited to the daily-report/forms hub, Pasture Map, Add Feed, the public
// Equipment fueling/checklist hub + Fuel Supply, and Tasks.
export const LIGHT_ALLOWED_VIEWS: ViewAllowlist = new Set([
  'home', // Light portal home
  'webformhub', // /dailys hub (all daily sub-forms)
  'tasksWebform', // /dailys/tasks
  'addfeed', // /addfeed
  'pastureMap', // /pasture-map
  'weather', // /weather — header button entry point, every role
  'webform', // /webform-pigs legacy standalone pig daily
  'fuelingHub', // /equipment public fueling/checklist hub
  'fuelSupply', // /fuel-supply
  'tasks', // /tasks (current non-admin Tasks behavior)
  // CP2: Light reads all daily reports and edits/deletes only its own.
  // The per-program daily list+record views are reachable (the program
  // dashboards, herds/flocks/batches/feed are NOT). Each router shows the
  // list and the record page under one view name.
  'broilerdailys', // /broiler/dailys(+/<id>)
  'layerdailys', // /layer/dailys(+/<id>)
  'eggdailys', // /layer/eggs(+/<id>)
  'pigdailys', // /pig/dailys(+/<id>)
  'cattledailys', // /cattle/dailys(+/<id>)
  'sheepdailys', // /sheep/dailys(+/<id>)
  // Cattle Log: field log every authenticated role (incl. Light) can
  // view and add to. Same program-bounce carve-out as cattledailys —
  // VIEW_TO_PROGRAM maps cattlelog -> cattle but Light reaches it via
  // this allowlist (the full cattle module stays out of reach).
  'cattlelog', // /cattle/log
  'mySubmissions', // Legacy route repurposed as View Past Reports
  // Projects (mig 256): every active role sees the farm jobs, adds ideas and
  // works the jobs it is on; the server owns each write (owner decision D12).
  'projects', // /projects
  // NOTE: weighins is deliberately excluded — Light users do not submit
  // weigh-ins (typed /weighins fails closed to the portal, and the
  // Weigh-Ins tile on the /dailys hub is hidden for Light).
]);

export function canLightAccessView(v: string | undefined): boolean {
  return LIGHT_ALLOWED_VIEWS.has(v);
}

// Owner answer E (2026-10-09): Equipment Tech reaches Home, Equipment (the /fleet
// module, the fueling/checklist hub and Fuel Supply), Tasks and Pasture. These
// non-program livestock and daily-report surfaces are not program-gated, so
// they are refused by name; the livestock programs themselves are refused by
// canAccessProgram. /production is refused separately (owner answer D).
export const EQUIPMENT_TECH_DENIED_VIEWS: readonly string[] = Object.freeze([
  'webformhub', // /dailys daily-report hub
  'weighins', // /weighins field form
  'addfeed', // /addfeed
  'webform', // /webform-pigs legacy pig daily
  'mySubmissions', // /my-submissions (View Past Reports: daily reports)
  'animalHistory', // /animals-on-farm
  'weighinsessions', // /weigh-in-sessions/:id (its tag fixes write cattle/sheep directly)
]);

export function canEquipmentTechAccessView(v: string | undefined): boolean {
  return !v || !EQUIPMENT_TECH_DENIED_VIEWS.includes(v);
}

// The six daily-report surfaces. Each router serves that program's daily list
// and its record page under one view name, so this covers entry, list, detail
// and history for every program.
export const DAILY_REPORT_VIEWS: readonly string[] = Object.freeze([
  'broilerdailys', // /broiler/dailys(+/<id>)
  'layerdailys', // /layer/dailys(+/<id>)
  'eggdailys', // /layer/eggs(+/<id>)
  'pigdailys', // /pig/dailys(+/<id>)
  'cattledailys', // /cattle/dailys(+/<id>)
  'sheepdailys', // /sheep/dailys(+/<id>)
]);

// Whether this caller reaches daily reports for EVERY program irrespective of
// program_access. Exported so the nav can show a daily-only section strip for a
// program the caller is not otherwise listed against, instead of leaving them a
// page they can open but cannot navigate.
export function canAccessDailyReports(authState: AuthState): boolean {
  return !!authState && authState.role === 'farm_team';
}

// Whether this caller can actually reach ONE daily-report view — by the Light
// allowlist, by the farm_team carve-out, or by holding the program outright.
// It mirrors the three ways resolveSurfaceAccess below reaches ALLOW for these
// views, so a caller offering a link can avoid offering one that bounces.
export function canReachDailyReportView(authState: AuthState, view: string, program: string): boolean {
  if (!DAILY_REPORT_VIEWS.includes(view)) return false;
  // Light first: canAccessProgram denies every program for Light, but the
  // allowlist reaches these views anyway.
  if (!!authState && authState.role === 'light') return canLightAccessView(view);
  if (canAccessDailyReports(authState)) return true;
  return canAccessProgram(authState, program);
}

// Daily-report form id -> (view, program), for the /dailys hub's per-program
// History links. The hub receives only the ids it may offer, so a link that
// would bounce is never rendered — the same rule the section strip follows.
export const DAILY_HISTORY_TARGETS: readonly {id: string; view: string; program: string}[] = Object.freeze([
  {id: 'broiler', view: 'broilerdailys', program: 'broiler'},
  {id: 'layer', view: 'layerdailys', program: 'layer'},
  {id: 'egg', view: 'eggdailys', program: 'layer'},
  {id: 'pig', view: 'pigdailys', program: 'pig'},
  {id: 'cattle', view: 'cattledailys', program: 'cattle'},
  {id: 'sheep', view: 'sheepdailys', program: 'sheep'},
]);

export function visibleDailyHistoryPrograms(authState: AuthState): string[] {
  return DAILY_HISTORY_TARGETS.filter((t) => canReachDailyReportView(authState, t.view, t.program)).map((t) => t.id);
}

// Roles program_access does not limit (owner decision 2026-10-15: management
// sees every program, everything outside the Admin tab). Mirrors the server's
// _activity_can_read program branches. Admin-only surfaces are role-gated
// elsewhere and are unaffected by this list.
export const PROGRAM_UNRESTRICTED_ROLES: readonly string[] = Object.freeze(['admin', 'management']);

export function canAccessProgram(authState: AuthState, prog: string | null | undefined): boolean {
  if (!prog) return true;
  if (!authState || !authState.profile) return true;
  if (PROGRAM_UNRESTRICTED_ROLES.includes(authState.role ?? '')) return true;
  // Light users are contained to the field portal + allowed form/Tasks
  // surfaces; no program (including the internal /fleet equipment module)
  // is reachable. Their Equipment access is the public fueling/checklist
  // hub (fuelingHub), which is not program-gated.
  if (authState.role === 'light') return false;
  // Migration 218: a positively loaded inactive profile reaches no program.
  // The server refuses every table to it; App renders InactiveAccountScreen
  // instead of any program page. The profile-not-loaded tolerance above is
  // untouched: the fail-closed 'inactive' of a FAILED profile load carries no
  // profile and keeps its header-only recovery shell.
  if (authState.role === 'inactive') return false;
  // Owner answer E (2026-10-09): Equipment Tech's menu is Home, Equipment,
  // Tasks and Pasture. Of the programs it holds only Equipment (/fleet); the
  // livestock programs would offer it buttons the server now refuses.
  const list = authState.profile.program_access;
  if (authState.role === 'equipment_tech') {
    // ...and Equipment itself still follows program_access like every program.
    return prog === 'equipment' && (!Array.isArray(list) || list.length === 0 || list.includes(prog));
  }
  if (!Array.isArray(list) || list.length === 0) return true;
  return list.includes(prog);
}

// Home's "Last 5 Days — All Daily Reports" feed: every program's recent daily
// reports in one block. Shown to the roles program_access does not limit
// (owner decision 2026-10-15: management sees everything outside the Admin
// tab). Its rows are already loaded for every role; this decides only whether
// the block renders.
export function canSeeAllDailyReportsFeed(authState: AuthState): boolean {
  return !!authState && PROGRAM_UNRESTRICTED_ROLES.includes(authState.role ?? '');
}

// The standard Home's livestock sections — Animals on Farm, Missing Data,
// Missed Daily Reports (and its per-row / Clear All writes) and Next 30 Days —
// are livestock surfaces. Owner answer E (2026-10-09) gives Equipment Tech
// Home, Equipment, Tasks and Pasture and refuses the livestock programs, so
// the tech's Home shows the equipment sections only. Same shape as
// canSeeAllDailyReportsFeed: the rows are already read (fail-closed for the
// role server-side); this decides only whether the blocks render.
export function canSeeLivestockHomeSections(authState: AuthState): boolean {
  return !!authState && authState.role !== 'equipment_tech';
}

// The /weigh-in-sessions/<id> page's species gate: whether its detail reads may
// be issued for a session of this species. Moved out of WeighInSessionPage so
// the program-unrestricted roles are named once. Every other role keeps the
// page's prior rule exactly: no species denies; a missing profile tolerates; an
// absent or empty list is every program; otherwise the list must name the
// species. (Equipment Tech never reaches the page: resolveSurfaceAccess refuses
// 'weighinsessions' to it by name.)
export function canAccessWeighInSpecies(authState: AuthState, species: string | null | undefined): boolean {
  if (!species) return false;
  if (!authState || !authState.profile) return true;
  if (PROGRAM_UNRESTRICTED_ROLES.includes(authState.role ?? '')) return true;
  const list = authState.profile.program_access;
  if (!Array.isArray(list) || list.length === 0) return true;
  return list.includes(species);
}

// Whether this session belongs to a deactivated account: a POSITIVELY loaded
// profile whose role is 'inactive' (migration 218). Such an account is refused
// by every table, Storage and its login is banned; the app shows it one plain
// "account inactive" card and starts no data request.
//
// The profile requirement is load-bearing. App's fail-closed path for a FAILED
// profile load also sets role 'inactive' but carries no profile -- that state
// keeps its own header-only recovery shell and Retry banner, because the role
// there is unknown, not inactive. A role preview of 'inactive' (admin only)
// does match, so the preview shows exactly what an inactive user sees.
export function isInactiveAccount(authState: AuthState): boolean {
  return !!authState && !!authState.profile && authState.role === 'inactive';
}

// ============================================================================
// resolveSurfaceAccess — B3.3 Prerequisite C
// ============================================================================
// One pure decision, used twice: by the render path before a data boundary can
// mount, and by the effects that repair view/URL state. Those effects were
// three separate conditionals in App() plus a fourth fail-closed render guard,
// each re-deriving `isLight`, `role` and the cattle-log carve-out on its own.
// A route-local loader cannot be introduced safely on top of four independent
// answers to the same question.
//
// This function does NOT change navigation. `navigation` reproduces the prior
// behaviour exactly, and the differential guard proves it across the whole
// role x view matrix. What it adds is `mayLoadProgramData`, a STRICTER and
// separate answer for loaders.
//
// The distinction is load-bearing. canAccessProgram() returns TRUE when
// authState.profile is absent (see above), so a profile that timed out or
// errored reads as "full program access". That is deliberately tolerant for
// navigation — bouncing a real operator to Home mid-load would be worse — but
// it is fail-OPEN for data. A route resource keyed on it would issue program
// requests for a user whose privileges are unknown. So loaders ask
// mayLoadProgramData, which requires a positively loaded profile, and a
// profile failure starts no program resource at all.
// ============================================================================

export const SURFACE_NAVIGATION = Object.freeze({
  ALLOW: 'allow', // render the requested view
  LIGHT_PORTAL: 'light-portal', // Light user off their allowlist -> portal
  BOUNCE_HOME: 'bounce-home', // program/processing denial -> setView('home')
} as const);

export type SurfaceNavigation = (typeof SURFACE_NAVIGATION)[keyof typeof SURFACE_NAVIGATION];
export type SurfaceAccessReason =
  | 'light-containment'
  | 'program-access'
  | 'processing-role'
  | 'production-role'
  | 'equipment-tech-scope'
  | null;

export interface SurfaceAccessInput {
  authState: AuthState;
  view?: string;
  viewToProgram: Readonly<Record<string, string>>;
  isProcessingRole: (role: string) => boolean;
}

export interface SurfaceAccess {
  navigation: SurfaceNavigation;
  reason: SurfaceAccessReason;
  program: string | null;
  isLight: boolean;
  profileLoaded: boolean;
  mayLoadProgramData: boolean;
}

export function resolveSurfaceAccess({
  authState,
  view,
  viewToProgram,
  isProcessingRole,
}: SurfaceAccessInput): SurfaceAccess {
  const role = authState ? authState.role : undefined;
  const isLight = !!authState && role === 'light';
  const program = view ? viewToProgram[view] : undefined;

  // A profile is "positively loaded" only when the session resolved AND
  // carries a profile row. authState === false is signed-out; null/undefined
  // is still resolving.
  const profileLoaded = !!authState && !!authState.profile;

  const lightAllowed = !!(isLight && canLightAccessView(view));
  // Cattle Log is role-gated, not program-gated: the server RPCs check role
  // only, so a farm_team user whose program_access excludes cattle still
  // reaches /cattle/log. equipment_tech and inactive do not, because the
  // server rejects them anyway.
  //
  // These two statements are deliberately SELF-CONTAINED — the role list is an
  // inline literal rather than a shared module const. The route oracle lifts
  // them out of this file as text and compiles them with `new Function` to
  // derive, by probing every view x role pair, which views the live expression
  // can actually exempt. Hoisting the array into a module binding would make
  // the compiled function throw ReferenceError and silently cost that proof.
  const isCattleLogRole = !!role && !['light', 'equipment_tech', 'inactive'].includes(role);
  const cattleLogExempt = view === 'cattlelog' && isCattleLogRole;

  // Daily reports are role-gated, not program-gated (product decision,
  // 2026-09-08): a farm_team user files and reads every program's daily report
  // regardless of program_access. canAccessProgram already treats a null or
  // empty list as full access, so this only changes a profile carrying a narrow
  // list, which previously could not open another program's daily surfaces at
  // all.
  //
  // Scope is the six daily-report surfaces and nothing else. Program homes,
  // herds/flocks, batches, feed, breeding, forecast, weigh-ins and the internal
  // /fleet module stay program-gated — this is a carve-out for daily reporting,
  // not a promotion of the role.
  //
  // farm_team only. admin and management are absent because canAccessProgram
  // short-circuits for them already (PROGRAM_UNRESTRICTED_ROLES).
  const dailyExempt = role === 'farm_team' && !!view && DAILY_REPORT_VIEWS.includes(view);

  let navigation: SurfaceNavigation = SURFACE_NAVIGATION.ALLOW;
  let reason: SurfaceAccessReason = null;

  // Order mirrors the render path: the Light fail-closed guard ran before the
  // program bounce could matter, and the processing bounce is independent.
  if (isLight && view && !canLightAccessView(view)) {
    navigation = SURFACE_NAVIGATION.LIGHT_PORTAL;
    reason = 'light-containment';
  } else if (program && !canAccessProgram(authState, program) && !lightAllowed && !cattleLogExempt && !dailyExempt) {
    navigation = SURFACE_NAVIGATION.BOUNCE_HOME;
    reason = 'program-access';
  } else if (view === 'processing' && role && !isProcessingRole(role)) {
    navigation = SURFACE_NAVIGATION.BOUNCE_HOME;
    reason = 'processing-role';
  } else if (view === 'production' && role && !PRODUCTION_ROLES.includes(role)) {
    // Owner answer D (2026-10-08): /production is hidden from Equipment Tech.
    // Gated on a RESOLVED role, like the processing branch above.
    navigation = SURFACE_NAVIGATION.BOUNCE_HOME;
    reason = 'production-role';
  } else if (role === 'equipment_tech' && !canEquipmentTechAccessView(view)) {
    // Owner answer E (2026-10-09): the daily-report hub, weigh-ins, add-feed,
    // past reports and animals-on-farm are outside Equipment Tech's menu.
    navigation = SURFACE_NAVIGATION.BOUNCE_HOME;
    reason = 'equipment-tech-scope';
  }

  // The loader gate. Stricter than navigation on exactly ONE axis: the profile
  // must be positively loaded.
  //
  // It deliberately does NOT re-derive authorization. `navigation` already
  // encodes every exemption — the Light allowlist and the cattle-log carve-out
  // both produce ALLOW for surfaces where canAccessProgram() is false. An
  // earlier version re-tested canAccessProgram here and so denied data to
  // exactly the users the exemptions exist to serve: a Light user reached
  // /broiler/dailys and its loader refused forever, and a farm_team user whose
  // program_access excludes cattle reached /cattle/log with the same result.
  //
  // The `!program` short-circuit was wrong in the other direction too: a
  // signed-out or still-resolving session returned true for every non-program
  // surface, because the profile check was skipped entirely rather than
  // applied first.
  //
  // Two rules, in this order: navigation must allow the surface, and the
  // profile must have loaded. Nothing else.
  const mayLoadProgramData = navigation === SURFACE_NAVIGATION.ALLOW && profileLoaded;

  return {navigation, reason, program: program ?? null, isLight, profileLoaded, mayLoadProgramData};
}

// ============================================================================
// Server allowlist mirrors (migration 221, S2 slice 2)
// ============================================================================
// Each list below mirrors ONE SECURITY DEFINER function's closed role
// allowlist, so a page hides an action -- or skips a read -- the server would
// refuse, instead of offering a control that errors (Design Constitution P01).
// The server stays the enforcement of record; these only decide what renders.
// Like the server, every list is closed: inactive, a missing role and any role
// added later get nothing until a list names them.

function roleOf(authState: AuthState): string | undefined {
  return authState ? authState.role || undefined : undefined;
}

function roleIn(authState: AuthState, roles: readonly string[]): boolean {
  const role = roleOf(authState);
  return !!role && roles.includes(role);
}

// list_production_legacy_events (migration 125). Owner answer D: /production is
// hidden from Equipment Tech; Light is contained to its portal anyway.
export const PRODUCTION_ROLES: readonly string[] = Object.freeze(['admin', 'management', 'farm_team']);

export function canAccessProduction(authState: AuthState): boolean {
  return roleIn(authState, PRODUCTION_ROLES);
}

// transfer_cattle_animal / transfer_sheep_animal, upsert/delete
// cattle breeding cycles, delete cattle calving / sheep lambing records.
// Equipment Tech is read-only on the livestock pages it can open (D3).
export const LIVESTOCK_RECORD_WRITE_ROLES: readonly string[] = Object.freeze(['admin', 'management', 'farm_team']);

export function canWriteLivestockRecords(authState: AuthState): boolean {
  return roleIn(authState, LIVESTOCK_RECORD_WRITE_ROLES);
}

// admin_delete_equipment_fueling. Owner answer B: Equipment Tech sees every
// fueling and checklist entry but deletes none -- neither a whole fueling row
// nor one recorded service-interval (checklist) entry on it.
export const EQUIPMENT_FUELING_DELETE_ROLES: readonly string[] = Object.freeze(['admin', 'management', 'farm_team']);

export function canDeleteEquipmentFuelingLog(authState: AuthState): boolean {
  return roleIn(authState, EQUIPMENT_FUELING_DELETE_ROLES);
}

// list_pig_breeder_imported_notes.
export const IMPORTED_BREEDER_NOTES_ROLES: readonly string[] = Object.freeze(['admin', 'management', 'farm_team']);

export function canReadImportedBreederNotes(authState: AuthState): boolean {
  return roleIn(authState, IMPORTED_BREEDER_NOTES_ROLES);
}

// _activity_can_read's per-role entity scope (comments + record Activity).
// admin, management and farm_team are scoped by record type only through the
// server's own existence and program checks; Light reads the record types its
// screens show (D2) and Equipment Tech reads Tasks and the equipment record (D3).
// Both also read Projects jobs (migration 256 adds project.job for all five roles).
export const LIGHT_RECORD_ACTIVITY_TYPES: readonly string[] = Object.freeze([
  'poultry.daily',
  'layer.daily',
  'egg.daily',
  'pig.daily',
  'cattle.daily',
  'sheep.daily',
  'task.instance',
  'task.template',
  'task.system_rule',
  'todo.item',
  'cattle.log',
  'equipment.item',
  'project.job',
]);
export const EQUIPMENT_TECH_RECORD_ACTIVITY_TYPES: readonly string[] = Object.freeze([
  'task.instance',
  'task.template',
  'task.system_rule',
  'equipment.item',
  'project.job',
]);

export function canReadRecordActivity(authState: AuthState, entityType: string | null | undefined): boolean {
  const role = roleOf(authState);
  if (!role || !entityType) return false;
  if (role === 'admin' || role === 'management' || role === 'farm_team') return true;
  if (role === 'light') return LIGHT_RECORD_ACTIVITY_TYPES.includes(entityType);
  if (role === 'equipment_tech') return EQUIPMENT_TECH_RECORD_ACTIVITY_TYPES.includes(entityType);
  return false;
}

// ============================================================================
// Table policy mirrors (migration 222, S2 slice 3)
// ============================================================================
// Migration 222 gives each role exactly the DIRECT table access its approved
// cells name. These lists mirror the policies a screen's direct writes hit, so
// the screen hides what the database would refuse (Design Constitution P01).
// Closed, like the server: inactive, a missing role and any later role get
// nothing.

// cattle / sheep INSERT + UPDATE, cattle_calving_records / sheep_lambing_records
// INSERT, cattle_origins INSERT. Light and Equipment Tech read these records
// but write none of them (D2, D3).
export const LIVESTOCK_TABLE_WRITE_ROLES: readonly string[] = Object.freeze(['admin', 'management', 'farm_team']);

export function canWriteLivestockTables(authState: AuthState): boolean {
  return roleIn(authState, LIVESTOCK_TABLE_WRITE_ROLES);
}

// Herds / Flocks Bulk Import (owner decision D4): cattle_breeds, sheep_breeds and
// sheep_origins INSERT plus import_cattle_comment / import_sheep_comment admit
// admin and management only.
export const LIVESTOCK_BULK_IMPORT_ROLES: readonly string[] = Object.freeze(['admin', 'management']);

export function canBulkImportLivestock(authState: AuthState): boolean {
  return roleIn(authState, LIVESTOCK_BULK_IMPORT_ROLES);
}
